import React, { useState, useEffect, useRef } from 'react';
import InputField from './common/InputField';
import PhoneInput from 'react-phone-number-input';
import Select from 'react-select';
import 'react-phone-number-input/style.css';
import { Button } from './common/Button';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { countries, generateAccountNumber } from '../utils/helpers';
import { addDemoAccountnoUser } from '../api/profile';
import { verifyPhoneNumber, sendPhoneVerificationCode } from '../api/auth';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import Modal from './common/Modal';

const DemoAccountForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        country: '',
        account_number: generateAccountNumber(),
        countryCode: 'US',
        phoneNumber: '',
        experience: '',
        expectedInvestment: '',
        terms1: false,
        terms2: false,
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [countryCode, setCountryCode] = useState('US');

    // Phone verification states
    const [otp, setOtp] = useState('');
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [showOtpModal, setShowOtpModal] = useState(false);

    // Detect user's country based on IP address
    useEffect(() => {
        const fetchCountryCode = async () => {
            try {
                const response = await axios.get('https://ipapi.co/country/');
                setCountryCode(response.data);
            } catch (error) {
                console.error('Failed to detect country code:', error);
            }
        };

        fetchCountryCode();
    }, []);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const executeRecaptcha = async (action = 'demoAccountForm') => {
        try {
            if (!window.grecaptcha) {
                throw new Error('reCAPTCHA not loaded');
            }
            return new Promise((resolve, reject) => {
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(
                        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
                        { action }
                    ).then((token) => {
                        resolve(token);
                    }).catch((error) => {
                        console.error('reCAPTCHA execution failed:', error);
                        reject(null);
                    });
                });
            });
        } catch (error) {
            console.error('reCAPTCHA execution failed:', error);
            return null;
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
        setErrors({ ...errors, [name]: '' });
    };

    const handlePhoneChange = (value) => {
        setFormData({
            ...formData,
            phoneNumber: value,
        });
        setErrors({ ...errors, phoneNumber: '' });
        setIsPhoneVerified(false);
        setOtpSent(false);
    };

    const handleCountryChange = (selectedOption) => {
        setFormData({
            ...formData,
            country: selectedOption.label,
            countryCode: selectedOption.value,
        });
    };

    const handleExperienceChange = (selectedOption) => {
        setFormData({
            ...formData,
            experience: selectedOption.value,
        });
    };

    const handleInvestmentChange = (selectedOption) => {
        setFormData({
            ...formData,
            expectedInvestment: selectedOption.value,
        });
    };

    const handleVerifyClick = async () => {
        if (!formData.phoneNumber) {
            setErrors({ phoneNumber: 'Phone number is required' });
            return;
        }

        setIsSendingOtp(true);
        setOtpError('');

        try {
            const recaptchaToken = await executeRecaptcha('requestOtp');
            if (!recaptchaToken) {
                setOtpError('Failed to verify you are human. Please try again.');
                return;
            }

            await sendPhoneVerificationCode(formData.phoneNumber, recaptchaToken);
            setOtpSent(true);
            setCountdown(60);
            setShowOtpModal(true);
        } catch (error) {
            setOtpError(error.message || 'Failed to send verification code');
        } finally {
            setIsSendingOtp(false);
        }
    };

    const isPhoneNumberValid = () => {
        return formData.phoneNumber && formData.phoneNumber.replace(/\D/g, '').length > 5;
    };

    const handleVerifyOtp = async () => {
        if (!otp) {
            setOtpError('Verification code is required');
            return;
        }

        if (otp.length < 6) {
            setOtpError('Verification code must be 6 digits');
            return;
        }

        setLoading(true);
        setOtpError('');

        try {
            const recaptchaToken = await executeRecaptcha('verifyOtp');
            if (!recaptchaToken) {
                setOtpError('Failed to verify you are human. Please try again.');
                return;
            }

            await verifyPhoneNumber(formData.phoneNumber, otp, recaptchaToken);
            setIsPhoneVerified(true);
            setOtp('');
            setOtpSent(false);
            setShowOtpModal(false);
        } catch (error) {
            setOtpError(error.message || 'Invalid verification code');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (countdown > 0) return;

        setIsSendingOtp(true);
        setOtpError('');

        try {
            const recaptchaToken = await executeRecaptcha('requestOtp');
            if (!recaptchaToken) {
                setOtpError('Failed to verify you are human. Please try again.');
                return;
            }

            await sendPhoneVerificationCode(formData.phoneNumber, recaptchaToken);
            setCountdown(60);
        } catch (error) {
            setOtpError(error.message || 'Failed to resend verification code');
        } finally {
            setIsSendingOtp(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!isPhoneVerified) {
            setErrors({ phoneNumber: 'Please verify your phone number' });
            setLoading(false);
            return;
        }

        // Execute reCAPTCHA v3 and get token
        const recaptchaToken = await executeRecaptcha();
        if (!recaptchaToken) {
            setErrors({ general: 'Failed to verify you are human. Please try again.' });
            setLoading(false);
            return;
        }

        // Validate terms and conditions
        if (!formData.terms1 || !formData.terms2) {
            setErrors({ general: 'You must accept both terms and conditions.' });
            setLoading(false);
            return;
        }

        try {
            const response = await addDemoAccountnoUser({ ...formData, recaptchaToken });
            if (!response.error) {
                navigate('/success');
            } else {
                setErrors({ general: response.message });
            }
        } catch (error) {
            setErrors({ general: error.message });
        } finally {
            setLoading(false);
        }
    };

    const experienceLevels = [
        { value: 'beginner', label: 'Beginner' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'advanced', label: 'Advanced' },
        { value: 'expert', label: 'Expert' },
    ];

    const investmentAmounts = [
        { value: '1000', label: '$1,000' },
        { value: '5000', label: '$5,000' },
        { value: '10000', label: '$10,000' },
        { value: '50000', label: '$50,000' },
        { value: '100000', label: '$100,000' },
        { value: 'other', label: 'Other' },
    ];

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md w-full max-w-3xl my-8">
                <h2 className="text-2xl font-bold mb-6 text-center text-text">Open Your Demo Account</h2>
                {errors.general && <p className="text-primary text-sm mb-4">{errors.general}</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                        label="First Name"
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="Enter your first name"
                        required
                        error={errors.firstName}
                    />
                    <InputField
                        label="Last Name"
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Enter your last name"
                        required
                        error={errors.lastName}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                        label="Email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        required
                        error={errors.email}
                    />
                    <div>
                        <label className="block text-text text-m font-bold mb-2">Country</label>
                        <Select
                            options={countries}
                            value={countries.find((country) => country.value === formData.countryCode)}
                            onChange={handleCountryChange}
                            placeholder="Select Country"
                            className="w-full text-gray-700"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.5rem',
                                    padding: '0.25rem',
                                    minHeight: '40px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        borderColor: '#e5e7eb',
                                    },
                                    '&:focus-within': {
                                        borderColor: '#9ca3af',
                                        boxShadow: '0 0 0 1px rgba(156, 163, 175, 0.5)',
                                    },
                                }),
                                input: (base) => ({
                                    ...base,
                                    padding: '0.25rem',
                                    margin: '0',
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    color: '#9ca3af',
                                }),
                                dropdownIndicator: (base) => ({
                                    ...base,
                                    padding: '0.25rem',
                                }),
                                indicatorSeparator: () => ({
                                    display: 'none',
                                }),
                                option: (base, { isFocused }) => ({
                                    ...base,
                                    backgroundColor: isFocused ? '#f3f4f6' : '#fff',
                                    color: '#111827',
                                }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-text text-m font-bold mb-2">Phone Number</label>
                    <div className="relative w-full">
                        <PhoneInput
                            international
                            defaultCountry={countryCode}
                            value={formData.phoneNumber}
                            onChange={handlePhoneChange}
                            placeholder="Enter your phone number"
                            disabled={isPhoneVerified}
                            className={`w-full pr-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors.phoneNumber ? 'border-primary' : 'border-gray-300'
                                } ${isPhoneVerified ? 'bg-gray-100 text-green-500' : ''}`}
                        />

                        {!isPhoneVerified && (
                            <button
                                type="button"
                                onClick={handleVerifyClick}
                                disabled={!isPhoneNumberValid() || isSendingOtp}
                                className={`absolute right-2 top-1/2 -translate-y-1/2 text-sm font-medium ${isPhoneNumberValid() ? 'text-primary hover:underline' : 'text-gray-400 cursor-not-allowed'
                                    } bg-white px-2 py-1 rounded`}
                            >
                                {isSendingOtp ? 'Sending...' : 'Verify'}
                            </button>
                        )}

                        {isPhoneVerified && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center text-green-500">
                                <AiOutlineCheck size={20} />
                            </div>
                        )}
                    </div>
                    {errors.phoneNumber && <p className="text-primary text-xs mt-1">{errors.phoneNumber}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-text text-m font-bold mb-2">Experience</label>
                        <Select
                            options={experienceLevels}
                            value={experienceLevels.find((exp) => exp.value === formData.experience)}
                            onChange={handleExperienceChange}
                            placeholder="Select Experience Level"
                            className="w-full text-gray-700"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.5rem',
                                    padding: '0.25rem',
                                    minHeight: '40px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        borderColor: '#e5e7eb',
                                    },
                                    '&:focus-within': {
                                        borderColor: '#9ca3af',
                                        boxShadow: '0 0 0 1px rgba(156, 163, 175, 0.5)',
                                    },
                                }),
                                input: (base) => ({
                                    ...base,
                                    padding: '0.25rem',
                                    margin: '0',
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    color: '#9ca3af',
                                }),
                                dropdownIndicator: (base) => ({
                                    ...base,
                                    padding: '0.25rem',
                                }),
                                indicatorSeparator: () => ({
                                    display: 'none',
                                }),
                                option: (base, { isFocused }) => ({
                                    ...base,
                                    backgroundColor: isFocused ? '#f3f4f6' : '#fff',
                                    color: '#111827',
                                }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    <div>
                        <label className="block text-text text-m font-bold mb-2">Expected Investment</label>
                        <Select
                            options={investmentAmounts}
                            value={investmentAmounts.find((inv) => inv.value === formData.expectedInvestment)}
                            onChange={handleInvestmentChange}
                            placeholder="Select Expected Investment"
                            className="w-full text-gray-700"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.5rem',
                                    padding: '0.25rem',
                                    minHeight: '40px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        borderColor: '#e5e7eb',
                                    },
                                    '&:focus-within': {
                                        borderColor: '#9ca3af',
                                        boxShadow: '0 0 0 1px rgba(156, 163, 175, 0.5)',
                                    },
                                }),
                                input: (base) => ({
                                    ...base,
                                    padding: '0.25rem',
                                    margin: '0',
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    color: '#9ca3af',
                                }),
                                dropdownIndicator: (base) => ({
                                    ...base,
                                    padding: '0.25rem',
                                }),
                                indicatorSeparator: () => ({
                                    display: 'none',
                                }),
                                option: (base, { isFocused }) => ({
                                    ...base,
                                    backgroundColor: isFocused ? '#f3f4f6' : '#fff',
                                    color: '#111827',
                                }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                </div>

                <div className="mt-4 mb-4">
                    <label className="flex items-start space-x-2">
                        <input
                            type="checkbox"
                            name="terms1"
                            checked={formData.terms1}
                            onChange={handleChange}
                            className="form-checkbox accent-[#4d4d4d] focus:ring-[#4d4d4d] mt-1"
                        />
                        <span className="text-text text-m leading-tight">
                            I accept the
                            <a href="https://investain.com/wp-content/uploads/2025/05/Terms-Conditions.pdf" className="text-primary hover:underline"> Terms & Conditions</a> and
                            <a href="https://investain.com/wp-content/uploads/2025/05/Risk-Disclosure.pdf" className="text-primary hover:underline"> Risk Disclosure</a>.
                        </span>
                    </label>
                </div>

                <div className="mb-6">
                    <label className="flex items-start space-x-2">
                        <input
                            type="checkbox"
                            name="terms2"
                            checked={formData.terms2}
                            onChange={handleChange}
                            className="form-checkbox accent-[#4d4d4d] focus:ring-[#4d4d4d] mt-1"
                        />
                        <span className="text-text text-m leading-tight">
                            I acknowledge that my information will be used in accordance with the
                            <a href="https://investain.com/privacy-policy" className="text-primary hover:underline"> Privacy Policy</a> and
                            <a href="https://investain.com/wp-content/uploads/2025/05/cookies_policy.pdf" className="text-primary hover:underline"> Cookie Policy</a>.
                        </span>
                    </label>
                </div>

                <div className="flex justify-end">
                    <Button 
                        type="submit" 
                        disabled={loading} 
                        className="g-recaptcha w-64"
                        data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        data-callback="onSubmit"
                        data-action="submit"
                    >
                        {loading ? 'Submitting...' : 'Open Demo Account'}
                    </Button>
                </div>
            </form>

            {showOtpModal && (
                <Modal isOpen={showOtpModal} onClose={() => setShowOtpModal(false)}>
                    <div className="bg-white p-4 rounded-lg max-w-sm w-full">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-lg font-bold text-gray-800">Verify Phone Number</h3>
                            <button
                                onClick={() => setShowOtpModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <AiOutlineClose size={18} />
                            </button>
                        </div>

                        <p className="text-sm text-gray-600 mb-3">
                            We've sent a 6-digit verification code to {formData.phoneNumber}
                        </p>

                        <div className="mb-3">
                            <label className="block text-gray-700 text-sm font-medium mb-1">
                                Verification Code
                            </label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="Enter 6-digit code"
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                maxLength={6}
                            />
                            {otpError && <p className="text-primary text-xs mt-1">{otpError}</p>}
                        </div>

                        <div className="flex justify-between items-center mb-4">
                            <p className="text-xs text-gray-600">
                                {countdown > 0 ? (
                                    `Resend code in ${countdown}s`
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        className="text-primary hover:underline text-xs"
                                    >
                                        Resend Code
                                    </button>
                                )}
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={handleVerifyOtp}
                            disabled={!otp || otp.length < 6 || loading}
                            className={`w-full py-2 px-4 rounded-lg text-white ${(!otp || otp.length < 6 || loading) ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'
                                }`}
                        >
                            {loading ? 'Verifying...' : 'Verify Phone Number'}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DemoAccountForm;